import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Phone, Mail, MapPin, Building2, Building, Calendar, Printer, Loader2 } from 'lucide-react';
import { useSuppliers, useDeleteSupplier } from '../../hooks/useSuppliers';
import { useDebounce } from '../../hooks/useDebounce';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { formatDateShort } from '../../lib/utils';
import { toast } from 'sonner';
import { exportarProveedoresPDF } from '../../api/suppliers';

const ITEMS_PER_PAGE = 20;

export function SupplierList() {
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 300);

  const { data: paginationData, isLoading, isError, refetch } = useSuppliers({
    search: debouncedSearch.trim() || undefined,
    page,
    limit: ITEMS_PER_PAGE,
  });

  const deleteSupplier = useDeleteSupplier();

  const suppliers = paginationData?.data || [];
  const totalItems = paginationData?.total || 0;
  const totalPages = paginationData?.totalPages || 1;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const calcularDias = (fechaRegistro?: string | Date) => {
    if (!fechaRegistro) return 0;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const registro = new Date(fechaRegistro);
    registro.setHours(0, 0, 0, 0);

    const diffTime = hoy.getTime() - registro.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getAniversarioTexto = (dias: number) => {
    if (dias === 0) return ' ¡Hoy es su día!';
    if (dias === 1) return ' 1 día como proveedor';
    return ` ${dias} días como proveedor`;
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este proveedor?')) return;
    try {
      await deleteSupplier.mutateAsync(id);
      refetch();
    } catch (error) {
      console.error('Error en handleDelete:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsExporting(true);
      toast.info('Generando PDF de proveedores...');

      const pdfUrl = await exportarProveedoresPDF(search);

      window.open(pdfUrl, '_blank');
      toast.success('PDF generado exitosamente');
    } catch (error: any) {
      if (error.response?.status === 404) {
        toast.warning('No hay proveedores para exportar');
      } else {
        toast.error('Error al generar el PDF');
        console.error(error);
      }
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando proveedores...</div>
      </div>
    );
  }

  if (isError) {
    return <div className="text-center py-8 text-red-500">Error al cargar los proveedores</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Proveedores</h1>
          <p className="text-sm text-gray-500">{totalItems} proveedor(es) registrados</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={isExporting || totalItems === 0}
            className="border-blue-200 text-blue-600 hover:bg-blue-50"
          >
            {isExporting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Printer size={16} />
            )}
            {isExporting ? 'Generando...' : 'Exportar PDF'}
          </Button>

          <Link to="/proveedores/nuevo">
            <Button>
              <Plus size={18} />
              Nuevo Proveedor
            </Button>
          </Link>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          type="text"
          placeholder="Buscar por nombre, CUIL, teléfono o localidad..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.map((supplier) => {
          const dias = calcularDias(supplier.fechaRegistro);

          return (
            <div key={supplier.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building2 className="text-purple-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{supplier.nombre}</h3>
                      {supplier.cuil && (
                        <p className="text-xs text-gray-400">CUIL: {supplier.cuil}</p>
                      )}
                      <p className={`text-xs font-medium mt-0.5 ${dias === 0 ? 'text-green-600' : 'text-blue-600'}`}>
                        <Calendar size={12} className="inline mr-1" />
                        {getAniversarioTexto(dias)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Link to={`/proveedores/editar/${supplier.id}`} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded">
                    <Edit size={16} />
                  </Link>
                  <button onClick={() => handleDelete(supplier.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="mt-3 space-y-1.5 text-sm">
                {supplier.telefono && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone size={14} className="text-gray-400" />
                    <span>{supplier.telefono}</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail size={14} className="text-gray-400" />
                    <span className="truncate">{supplier.email}</span>
                  </div>
                )}
                {supplier.direccion && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={14} className="text-gray-400" />
                    <span className="truncate">{supplier.direccion}</span>
                  </div>
                )}
                {supplier.localidad && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building size={14} className="text-gray-400" />
                    <span>{supplier.localidad}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                Registrado: {formatDateShort(supplier.fechaRegistro)}
              </div>
            </div>
          );
        })}
      </div>

      {suppliers.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p>No se encontraron proveedores</p>
        </div>
      )}

      {totalItems > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}